import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Loader2, Minus, Plus, Clock, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function ReservationDialog({
  open, onClose,
  restaurant, tc,
  data, onDataChange,
  confirmed,
  submitting,
  onSubmit,
  availableDates,
  getAvailableTimeSlots,
  slotAlternatives,
  onSelectAlternative,
}) {
  const { t } = useTranslation();
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [pr, pg, pb] = tc.primary.replace("#","").match(/.{2}/g).map(h => parseInt(h,16));

  // Hide chat widget when reservation dialog is open
  useEffect(() => {
    const chatWidget = document.getElementById('kaiso-chat-widget');
    if (chatWidget) {
      chatWidget.style.display = open ? 'none' : 'block';
    }
    return () => {
      const chatWidget = document.getElementById('kaiso-chat-widget');
      if (chatWidget) {
        chatWidget.style.display = 'block';
      }
    };
  }, [open]);

  return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-md bg-white max-h-[calc(100vh-2rem)] overflow-y-auto p-4 md:p-6">
          {confirmed ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `rgba(${pr},${pg},${pb},0.1)` }}><CalendarDays className="w-8 h-8" style={{ color: tc.primary }} /></div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{Number(data.time?.split(":")[0]) < 17 ? "Almuerzo reservado correctamente." : "Reserva enviada."}</h3>
              <p className="text-sm text-gray-500 mb-1">Sua reserva para <strong>{data.guests} pessoas</strong> em <strong>{data.date}</strong> as <strong>{data.time}</strong> foi recebida.</p>
              <p className="text-sm text-gray-500">Confirmaremos por {data.customer_email ? "email e " : ""}WhatsApp em breve.</p>
              <Button className="mt-4 text-white" style={{ backgroundColor: tc.primary }} onClick={() => onClose()}>Fechar</Button>
            </div>
          ) : slotAlternatives && slotAlternatives.length > 0 ? (
            /* ── Slot full — show alternatives ── */
            <div className="py-4">
              <DialogHeader>
                <DialogTitle className="text-base font-bold flex items-center gap-2 text-amber-700">
                  <Clock className="w-5 h-5" />
                  Horário indisponível
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-gray-600 mt-3 mb-4">
                O horário das <strong>{data.time}</strong> já está completo.
                Escolhe um dos horários disponíveis:
              </p>
              <div className="space-y-2">
                {slotAlternatives.map((slot) => (
                  <button
                    key={slot.time}
                    onClick={() => onSelectAlternative(slot.time)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition text-left"
                  >
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-gray-800">às {slot.time}</span>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {slot.remaining_people} lugar{slot.remaining_people !== 1 ? "es" : ""} disponível{slot.remaining_people !== 1 ? "eis" : ""}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t">
                <Button variant="outline" onClick={() => onClose()} className="w-full">Cancelar</Button>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader><DialogTitle className="text-lg font-bold flex items-center gap-2"><CalendarDays className="w-5 h-5" style={{ color: tc.primary }} />Reservar Mesa</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold">Nome *</Label><Input value={data.customer_name} onChange={e => onDataChange(p => ({ ...p, customer_name: e.target.value }))} placeholder="Seu nome" data-testid="res-name" /></div>
                  <div><Label className="text-xs font-semibold">Telefone *</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-2.5 rounded-l-md border border-r-0 border-gray-300 bg-gray-100 text-sm font-bold text-gray-700 select-none">{restaurant?.country_code === "PT" ? "+351" : restaurant?.country_code === "BR" ? "+55" : "+34"}</span>
                      <Input value={data.customer_phone} onChange={e => onDataChange(p => ({ ...p, customer_phone: e.target.value.replace(/[^\d\s]/g, "") }))} placeholder="912 345 678" className="rounded-l-none" data-testid="res-phone" />
                    </div>
                  </div>
                </div>
                <div><Label className="text-xs font-semibold">Email (opcional)</Label><Input type="email" value={data.customer_email} onChange={e => onDataChange(p => ({ ...p, customer_email: e.target.value }))} placeholder="seu@email.com" data-testid="res-email" /></div>
                <div>
                  <Label className="text-xs font-semibold">Data *</Label>
                  <Select value={data.date} onValueChange={v => onDataChange(p => ({ ...p, date: v, time: "" }))}>
                    <SelectTrigger data-testid="res-date"><SelectValue placeholder="Selecionar data" /></SelectTrigger>
                    <SelectContent>
                      {availableDates.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Hora *</Label>
                    <Select value={data.time} onValueChange={v => onDataChange(p => ({ ...p, time: v }))} disabled={!data.date}>
                      <SelectTrigger data-testid="res-time"><SelectValue placeholder="Seleccionar horario" /></SelectTrigger>
                      <SelectContent>
                        {(getAvailableTimeSlots?.(data.date) || []).map(slot => {
                          const time = slot.split("-")[0];
                          return <SelectItem key={slot} value={time}>{slot}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Pessoas *</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200" onClick={() => onDataChange(p => ({ ...p, guests: Math.max(1, p.guests - 1) }))}><Minus className="w-3.5 h-3.5" /></button>
                      <span className="w-8 text-center font-bold text-lg" data-testid="res-guests">{data.guests}</span>
                      <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200" onClick={() => onDataChange(p => ({ ...p, guests: Math.min(20, p.guests + 1) }))}><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Preferencia de lugar</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {[{ v: "sin_preferencia", l: "Sem pref." }, { v: "interior", l: "Interior" }, { v: "exterior", l: "Esplanada" }].map(o => (
                      <button key={o.v} onClick={() => onDataChange(p => ({ ...p, seating_preference: o.v }))}
                        className={`py-2 rounded-lg text-xs font-medium border-2 transition ${data.seating_preference === o.v ? "" : "border-gray-200 bg-gray-50 text-gray-500"}`}
                        style={data.seating_preference === o.v ? { borderColor: tc.primary, backgroundColor: `rgba(${pr},${pg},${pb},0.06)`, color: tc.primary } : {}}
                        data-testid={`res-seating-${o.v}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs font-semibold">Observacoes</Label><Textarea value={data.notes} onChange={e => onDataChange(p => ({ ...p, notes: e.target.value }))} placeholder="Ex: mesa na esplanada, aniversario, cadeirinha..." rows={2} data-testid="res-notes" /></div>

                {/* No-Show Policy Alert & Consent */}
                <div className="mt-4 pt-4 border-t">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-900">
                        <p className="font-semibold mb-1">
                          {t('publicMenu.reservation.policyTitle', 'Política de Reservas')}
                        </p>
                        <p className="text-xs leading-relaxed">
                          {t('publicMenu.reservation.policyText', 'As reservas são gratuitas e as vagas são limitadas. Caso não possa comparecer, solicitamos que informe o cancelamento com pelo menos 24 horas de antecedência.\n\nClientes que não comparecerem e não comunicarem o cancelamento dentro do prazo poderão ficar impedidos de realizar novas reservas por até 6 meses.')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="policy-accept"
                      checked={policyAccepted}
                      onChange={e => setPolicyAccepted(e.target.checked)}
                      className="mt-1 w-4 h-4 cursor-pointer"
                    />
                    <label htmlFor="policy-accept" className="text-xs text-gray-600 cursor-pointer">
                      {t('publicMenu.reservation.acceptPolicy', 'Li e concordo com a política de reservas')}
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => onClose()}>Cancelar</Button>
                <Button
                  onClick={() => {
                    // P0.7: Fix checkbox race condition — pass finalData directly to avoid setState async delay
                    const finalData = {
                      ...data,
                      policy_accepted: policyAccepted,
                      policy_channel: "public_web",
                    };
                    onSubmit(finalData);
                  }}
                  disabled={submitting || !policyAccepted}
                  className="text-white font-semibold"
                  style={{ backgroundColor: tc.primary, opacity: (submitting || !policyAccepted) ? 0.6 : 1 }}
                  data-testid="submit-reservation-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarDays className="w-4 h-4 mr-2" />}
                  Reservar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
  );
}
